import { useState, useEffect, useCallback, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import Cookie from 'js-cookie';

const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const isMountedRef = useRef(true);

  const checkAuth = useCallback(() => {
    if (!isMountedRef.current) return false;
    
    try {
      const token = Cookie.get('token');
      
      if (!token) {
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        return false;
      }

      const decodedToken = jwtDecode(token);
      const isTokenExpired = decodedToken.exp < Date.now() / 1000;

      if (isTokenExpired) {
        Cookie.remove('token');
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        return false;
      }

      setUser(decodedToken);
      setIsAuthenticated(true);
      setIsLoading(false);
      return true;
    } catch (error) {
      Cookie.remove('token');
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      return false;
    }
  }, []);

  useEffect(() => {
    checkAuth();
    return () => {
      isMountedRef.current = false;
    };
  }, [checkAuth]);

  const login = useCallback((token) => {
    Cookie.set('token', token, { expires: 1 });
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(() => {
    Cookie.remove('token');
    setUser(null);
    setIsAuthenticated(false);
    setIsLoading(false);
  }, []);

  const getToken = useCallback(() => {
    return Cookie.get('token');
  }, []);

  const getUserId = useCallback(() => {
    return user?.userId || null;
  }, [user]);

  const getUserName = useCallback(() => {
    return user?.name || '';
  }, [user]);

  return {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    checkAuth,
    getToken,
    getUserId,
    getUserName
  };
};

export default useAuth;
